import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, Card, CardContent, Chip, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, Slider, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import { useUserContext } from "../hooks/UserContext";
import { cambiarEstadoMonitor, MonitorConfiguracion, MonitorDestino, MonitorItem, obtenerConfiguracionMonitor, obtenerDestinosMonitor, obtenerItemsMonitor } from "../services/monitorComandasService";

const nombre = (e: number) => e === 0 ? "Pendiente" : e === 1 ? "En preparación" : e === 2 ? "Terminada" : "Anulada";
const color = (e: number): "warning" | "info" | "success" | "default" => e === 0 ? "warning" : e === 1 ? "info" : e === 2 ? "success" : "default";

const tituloOrigen = (item: MonitorItem) => {
  if (item.idMesa > 0) return `Mesa ${item.idMesa}`;
  return item.nombreComanda || "Sin nombre";
};

const claveComanda = (item: MonitorItem) => `${item.idLocal}:${item.pv}:${item.idComanda}`;

type AudioWindow = Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext };
type TipoCampanita = "doble" | "cristal" | "calida" | "larga";
type AvisoComanda = { id: number; titulo: string; detalle: string };

const tiposCampanita: Array<{ value: TipoCampanita; label: string }> = [
  { value: "doble", label: "Doble" },
  { value: "cristal", label: "Cristal" },
  { value: "calida", label: "Cálida" },
  { value: "larga", label: "Larga" },
];

const leerIntensidad = () => {
  const valor = Number(localStorage.getItem("monitorComandas.intensidad") || 1);
  return Number.isFinite(valor) ? Math.min(1.6, Math.max(0.4, valor)) : 1;
};

const leerTipoCampanita = (): TipoCampanita => {
  const valor = localStorage.getItem("monitorComandas.tipoSonido");
  return tiposCampanita.some((tipo) => tipo.value === valor) ? valor as TipoCampanita : "doble";
};

const AvisoComandaEntrante = ({ aviso, onClose }: { aviso: AvisoComanda; onClose: (id: number) => void }) => {
  useEffect(() => {
    const timer = window.setTimeout(() => onClose(aviso.id), 25000);
    return () => window.clearTimeout(timer);
  }, [aviso.id, onClose]);

  return (
    <Alert
      icon={<NotificationsActiveOutlinedIcon fontSize="inherit" />}
      severity="info"
      onClose={() => onClose(aviso.id)}
      elevation={4}
      sx={{ width: "100%", bgcolor: "background.paper", borderRadius: 2, alignItems: "center" }}
    >
      <Typography variant="subtitle2" fontWeight={700}>{aviso.titulo}</Typography>
      <Typography variant="body2">{aviso.detalle}</Typography>
    </Alert>
  );
};

const MonitorComandas = () => {
  const { user } = useUserContext();
  const [idLocal, setIdLocal] = useState(Number(localStorage.getItem("monitorComandas.local") || 0));
  const [destinos, setDestinos] = useState<MonitorDestino[]>([]);
  const [idDestino, setIdDestino] = useState(Number(localStorage.getItem("monitorComandas.destino") || 0));
  const [historial, setHistorial] = useState(false);
  const [items, setItems] = useState<MonitorItem[]>([]);
  const [error, setError] = useState("");
  const [procesando, setProcesando] = useState(false);
  const [configuracion, setConfiguracion] = useState<MonitorConfiguracion | null>(null);
  const [destinosCargados, setDestinosCargados] = useState(false);
  const [sonidoActivo, setSonidoActivo] = useState(() => localStorage.getItem("monitorComandas.sonido") !== "0");
  const [tipoCampanita, setTipoCampanita] = useState<TipoCampanita>(leerTipoCampanita);
  const [intensidad, setIntensidad] = useState(leerIntensidad);
  const audioContextRef = useRef<AudioContext | null>(null);
  const comandasConocidasRef = useRef<Set<string> | null>(null);
  const siguienteAvisoIdRef = useRef(1);
  const [avisosComandas, setAvisosComandas] = useState<AvisoComanda[]>([]);

  const cerrarAvisoComanda = useCallback((id: number) => {
    setAvisosComandas((actuales) => actuales.filter((aviso) => aviso.id !== id));
  }, []);

  const obtenerAudioContext = useCallback(() => {
    if (audioContextRef.current) return audioContextRef.current;
    const AudioContextClass = window.AudioContext || (window as AudioWindow).webkitAudioContext;
    if (!AudioContextClass) return null;
    audioContextRef.current = new AudioContextClass();
    return audioContextRef.current;
  }, []);

  const reproducirCampanita = useCallback(async (tipo: TipoCampanita, volumen: number) => {
    try {
      const contexto = obtenerAudioContext();
      if (!contexto) return;
      if (contexto.state === "suspended") await contexto.resume();

      const inicio = contexto.currentTime;
      const patrones: Record<TipoCampanita, Array<{ demora: number; duracion: number; frecuencias: number[] }>> = {
        doble: [
          { demora: 0, duracion: 0.7, frecuencias: [1046.5, 1568] },
          { demora: 0.38, duracion: 0.85, frecuencias: [1174.7, 1760] },
        ],
        cristal: [{ demora: 0, duracion: 1.15, frecuencias: [1318.5, 1975.5, 2637] }],
        calida: [
          { demora: 0, duracion: 0.95, frecuencias: [784, 1174.7] },
          { demora: 0.28, duracion: 0.9, frecuencias: [880, 1320] },
        ],
        larga: [{ demora: 0, duracion: 1.65, frecuencias: [880, 1320, 1760] }],
      };

      patrones[tipo].forEach((tono) => {
        const comienza = inicio + tono.demora;
        const ganancia = contexto.createGain();
        ganancia.gain.setValueAtTime(0.0001, comienza);
        ganancia.gain.exponentialRampToValueAtTime(0.032 * volumen, comienza + 0.015);
        ganancia.gain.exponentialRampToValueAtTime(0.0001, comienza + tono.duracion);
        ganancia.connect(contexto.destination);

        tono.frecuencias.forEach((frecuencia, indice) => {
          const oscilador = contexto.createOscillator();
          oscilador.type = "sine";
          oscilador.frequency.setValueAtTime(frecuencia, comienza);
          oscilador.detune.setValueAtTime(indice === 0 ? 0 : -4 * indice, comienza);
          oscilador.connect(ganancia);
          oscilador.start(comienza);
          oscilador.stop(comienza + tono.duracion + 0.03);
        });
      });
    } catch {
      // Algunos navegadores requieren una interacción previa para habilitar audio.
    }
  }, [obtenerAudioContext]);

  const sonarCampanita = useCallback(() => {
    if (sonidoActivo) void reproducirCampanita(tipoCampanita, intensidad);
  }, [intensidad, reproducirCampanita, sonidoActivo, tipoCampanita]);

  useEffect(() => {
    if (!sonidoActivo) return;
    const habilitarAudio = () => { void obtenerAudioContext()?.resume(); };
    window.addEventListener("pointerdown", habilitarAudio, { once: true });
    return () => window.removeEventListener("pointerdown", habilitarAudio);
  }, [obtenerAudioContext, sonidoActivo]);

  useEffect(() => {
    if (!user) return;
    obtenerConfiguracionMonitor(user).then((c) => {
      setConfiguracion(c);
      setIdLocal(c.idLocal ?? 0);
    }).catch((e) => setError(e.message));
  }, [user]);

  useEffect(() => {
    if (!user || !configuracion || !configuracion.activo) {
      setDestinos([]);
      setDestinosCargados(false);
      return;
    }
    setDestinosCargados(false);
    localStorage.setItem("monitorComandas.local", String(idLocal));
    obtenerDestinosMonitor(user, idLocal).then((d) => {
      setDestinos(d);
      if (!d.some((x) => x.idDestino === idDestino)) setIdDestino(d[0]?.idDestino || 0);
      setError("");
    }).catch((e) => setError(e.message))
      .finally(() => setDestinosCargados(true));
  }, [user, idLocal, idDestino, configuracion]);

  const refrescar = useCallback(async () => {
    if (!user || !configuracion?.activo || !idDestino) {
      setItems([]);
      return;
    }
    try {
      const siguientes = await obtenerItemsMonitor(user, idLocal, idDestino, historial);
      if (!historial) {
        const clavesActuales = new Set(siguientes.map(claveComanda));
        const conocidas = comandasConocidasRef.current;
        const clavesNuevas = conocidas ? Array.from(clavesActuales).filter((clave) => !conocidas.has(clave)) : [];
        if (clavesNuevas.length) {
          sonarCampanita();
          const nuevosAvisos = clavesNuevas.map((clave) => {
            const grupo = siguientes.filter((item) => claveComanda(item) === clave);
            const cabecera = grupo[0];
            return {
              id: siguienteAvisoIdRef.current++,
              titulo: `Nueva comanda · ${tituloOrigen(cabecera)}`,
              detalle: `${grupo.length} ${grupo.length === 1 ? "producto" : "productos"} para preparar`,
            };
          }).reverse();
          setAvisosComandas((actuales) => [...nuevosAvisos, ...actuales].slice(0, 3));
        }
        comandasConocidasRef.current = clavesActuales;
      }
      setItems(siguientes);
      setError("");
    } catch (e: any) {
      setError(e.message);
    }
  }, [user, idLocal, idDestino, historial, configuracion, sonarCampanita]);

  useEffect(() => {
    comandasConocidasRef.current = null;
  }, [idLocal, idDestino]);

  useEffect(() => {
    refrescar();
    const t = window.setInterval(refrescar, 5000);
    return () => window.clearInterval(t);
  }, [refrescar]);

  useEffect(() => {
    if (idDestino) localStorage.setItem("monitorComandas.destino", String(idDestino));
  }, [idDestino]);

  const alternarSonido = () => {
    const proximo = !sonidoActivo;
    setSonidoActivo(proximo);
    localStorage.setItem("monitorComandas.sonido", proximo ? "1" : "0");
    if (proximo) void obtenerAudioContext()?.resume();
  };

  const cambiarTipoCampanita = (tipo: TipoCampanita) => {
    setTipoCampanita(tipo);
    localStorage.setItem("monitorComandas.tipoSonido", tipo);
    void reproducirCampanita(tipo, intensidad);
  };

  const cambiarIntensidad = (valor: number) => {
    setIntensidad(valor);
    localStorage.setItem("monitorComandas.intensidad", String(valor));
  };

  const avanzar = async (item: MonitorItem) => {
    if (!user || item.estado >= 2) return;
    setProcesando(true);
    try {
      await cambiarEstadoMonitor(user, [item.idItem], idLocal, idDestino, item.estado + 1);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setProcesando(false);
      await refrescar();
    }
  };

  const grupos = useMemo(() => {
    const mapa = new Map<string, MonitorItem[]>();
    items.forEach((item) => {
      const clave = `${item.idLocal}:${item.pv}:${item.idComanda}`;
      mapa.set(clave, [...(mapa.get(clave) || []), item]);
    });
    return Array.from(mapa.values());
  }, [items]);

  const avanzarGrupo = async (grupo: MonitorItem[]) => {
    if (!user) return;
    const pendientes = grupo.filter((i) => i.estado === 0);
    const preparando = grupo.filter((i) => i.estado === 1);
    const objetivo = pendientes.length ? pendientes : preparando;
    if (!objetivo.length) return;
    setProcesando(true);
    try {
      await cambiarEstadoMonitor(user, objetivo.map((i) => i.idItem), idLocal, idDestino, objetivo[0].estado + 1);
    } catch (e: any) { setError(e.message); }
    finally { setProcesando(false); await refrescar(); }
  };

  return (
    <Box sx={{ pb: 3, userSelect: "none" }}>
      <Box
        aria-live="polite"
        sx={{
          position: "fixed",
          top: { xs: 72, sm: 80 },
          right: { xs: 8, sm: 16 },
          width: { xs: "calc(100vw - 16px)", sm: 400 },
          zIndex: (theme) => theme.zIndex.snackbar,
          pointerEvents: "none",
        }}
      >
        <Stack spacing={1} sx={{ pointerEvents: "auto" }}>
          {avisosComandas.map((aviso) => <AvisoComandaEntrante key={aviso.id} aviso={aviso} onClose={cerrarAvisoComanda} />)}
        </Stack>
      </Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} mb={2}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
          <RestaurantIcon color="primary" />
          <Typography variant="h4" fontWeight={700}>Monitor de comandas</Typography>
        </Stack>
        {configuracion?.idLocal != null && <Chip label={`Local configurado: ${configuracion.idLocal}`} color="primary" variant="outlined" />}
        {configuracion?.idLocal == null && <Chip label="Todos los locales" color="primary" variant="outlined" />}
        <Tooltip title={sonidoActivo ? "Silenciar campanita" : "Activar campanita"}>
          <IconButton onClick={alternarSonido} color={sonidoActivo ? "primary" : "default"} aria-label={sonidoActivo ? "Silenciar campanita" : "Activar campanita"}>
            {sonidoActivo ? <VolumeUpOutlinedIcon /> : <VolumeOffOutlinedIcon />}
          </IconButton>
        </Tooltip>
        {sonidoActivo && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: { xs: "100%", md: 300 } }}>
            <FormControl size="small" sx={{ minWidth: 112 }}>
              <InputLabel>Campanita</InputLabel>
              <Select value={tipoCampanita} label="Campanita" onChange={(e) => cambiarTipoCampanita(e.target.value as TipoCampanita)}>
                {tiposCampanita.map((tipo) => <MenuItem key={tipo.value} value={tipo.value}>{tipo.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ width: 105, px: 0.5 }}>
              <Typography variant="caption" color="text.secondary">Intensidad</Typography>
              <Slider
                size="small"
                min={0.4}
                max={1.6}
                step={0.2}
                value={intensidad}
                aria-label="Intensidad de la campanita"
                onChange={(_, valor) => cambiarIntensidad(valor as number)}
                onChangeCommitted={(_, valor) => { void reproducirCampanita(tipoCampanita, valor as number); }}
              />
            </Box>
            <Tooltip title="Probar campanita">
              <IconButton size="small" onClick={() => { void reproducirCampanita(tipoCampanita, intensidad); }} aria-label="Probar campanita">
                <PlayCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Destino</InputLabel>
          <Select value={idDestino || ""} label="Destino" onChange={(e) => setIdDestino(Number(e.target.value))}>
            {destinos.map((d) => <MenuItem key={d.idDestino} value={d.idDestino}>{configuracion?.idLocal == null ? `Local ${d.idLocal} · ${d.nombre}` : d.nombre}</MenuItem>)}
          </Select>
        </FormControl>
      </Stack>
      <Tabs value={historial ? 1 : 0} onChange={(_, v) => setHistorial(v === 1)} sx={{ mb: 2 }}>
        <Tab label="Activas" />
        <Tab label="Terminadas de hoy" />
      </Tabs>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Se muestran todas las comandas del local seleccionado, independientemente del punto de venta donde se hayan originado.
      </Typography>
      {configuracion && !configuracion.activo && <Alert severity="warning" sx={{ mb: 2 }}>El monitor de comandas está inactivo.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {configuracion?.activo && destinosCargados && destinos.length === 0 && !error && (
        <Alert severity="error">
          No hay destinos activos configurados. Configure al menos uno desde Parametrización &gt; Monitor Comandas.
        </Alert>
      )}
      {idDestino && !items.length && (
        <Alert severity="success">{historial ? "No hay comandas terminadas hoy." : "No hay comandas pendientes para este destino."}</Alert>
      )}
      <Grid container spacing={2}>
        {grupos.map((grupo) => {
          const cabecera = grupo[0];
          const operables = grupo.filter((i) => i.estado === 0 || i.estado === 1);
          const tienePendientes = operables.some((i) => i.estado === 0);
          return (
          <Grid item xs={12} md={6} lg={4} key={`${cabecera.idLocal}:${cabecera.pv}:${cabecera.idComanda}`}>
            <Card variant="outlined" sx={{ borderRadius: 3, height: "100%" }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h5" fontWeight={800}>{tituloOrigen(cabecera)}</Typography>
                  <Typography color="text.secondary">
                    {new Date(cabecera.fechaCreacion).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </Typography>
                </Stack>
                {cabecera.idMesa > 0 && cabecera.nombreComanda && (
                  <Typography color="primary" fontWeight={700} mb={1}>{cabecera.nombreComanda}</Typography>
                )}
                <Stack spacing={1}>
                {grupo.map((i) => <Box key={i.idItem} sx={{ p: 1.5, borderRadius: 2, bgcolor: "grey.50", border: "1px solid", borderColor: "grey.200" }}>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Typography variant="h6" fontWeight={700}>{i.cantidad} × {i.producto}</Typography>
                    <Chip size="small" label={nombre(i.estado)} color={color(i.estado)} />
                  </Stack>
                  {i.nota && <Typography sx={{ mt: 0.5, fontWeight: 700 }}>Nota: {i.nota}</Typography>}
                  {i.corregido && <Chip size="small" label="Corregido" color="warning" sx={{ mt: 1 }} />}
                  {!historial && i.estado < 2 && (
                    <Button
                      fullWidth
                      size="large"
                      variant="contained"
                      sx={{ mt: 1.2, minHeight: 48 }}
                      disabled={procesando}
                      onClick={() => avanzar(i)}
                    >
                      {i.estado === 0 ? "Iniciar preparación" : "Terminar"}
                    </Button>
                  )}
                </Box>)}
                </Stack>
                {!historial && operables.length > 1 && <Button fullWidth size="large" variant="outlined" sx={{ mt: 1.5, minHeight: 48 }} disabled={procesando} onClick={() => avanzarGrupo(grupo)}>
                  {tienePendientes ? "Iniciar comanda completa" : "Terminar comanda completa"}
                </Button>}
              </CardContent>
            </Card>
          </Grid>
        )})}
      </Grid>
    </Box>
  );
};

export default MonitorComandas;
